import React, { useEffect, useState } from "react";
import { Sparkles, Check, ArrowRight, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fmtINR, fmtNum } from "@/lib/api";
import GlassCard from "./GlassCard";

const REASONS = {
  A: { label: "Repeat + Renewed", cls: "copper" },
  B: { label: "Repeat customer", cls: "amber" },
  C: { label: "Standard", cls: "slate" },
};

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const buildWhatsAppLink = (m) => {
  const raw = (m.customer_contact || "").replace(/\D/g, "").replace(/^0+/, "").replace(/^91/, "");
  const phone = raw || "9000000000";
  const msg =
    `Hi ${m.customer_name},\n\n` +
    `Your ${m.model} (Serial: ${m.machine_serial}) has clocked ${Math.round(m.hmr)} HMR ` +
    `and is overdue for the EQP Engine Care package.\n\n` +
    `Opsora has flagged this machine as high priority for service. The Engine Care ` +
    `package protects your engine warranty and reduces breakdown risk significantly.\n\n` +
    `Reply YES to schedule a visit or call us directly.\n\n` +
    `– ${m.dealer_name}`;
  return `https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`;
};

const isSent = (serial) => {
  try {
    const t = localStorage.getItem(`opsora_wa_sent_${serial}`);
    if (!t) return false;
    return Date.now() - parseInt(t, 10) < SEVEN_DAYS_MS;
  } catch { return false; }
};

const markSent = (serial) => {
  try { localStorage.setItem(`opsora_wa_sent_${serial}`, String(Date.now())); } catch {}
};

export default function EngineCareCard() {
  const [data, setData] = useState(null);
  const [queued, setQueued] = useState(new Set());
  const [sentSerials, setSentSerials] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);

  useEffect(() => {
    api.get("/sales/engine-care-summary").then((r) => {
      setData(r.data);
      // Hydrate sent state from localStorage
      const init = new Set(
        r.data.targets.filter((t) => isSent(t.machine_serial)).map((t) => t.machine_serial)
      );
      setSentSerials(init);
    });
  }, []);

  const toggleQueue = (id) => {
    setQueued((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const sendWhatsApp = (m) => {
    window.open(buildWhatsAppLink(m), "_blank");
    markSent(m.machine_serial);
    setSentSerials((s) => new Set(s).add(m.machine_serial));
  };

  const sendAllHighPriority = async () => {
    if (!data) return;
    const priority = data.targets.filter((t) => t.priority === "A");
    if (priority.length === 0) {
      toast("No high-priority targets to send");
      return;
    }
    setBulkSending(true);
    for (const m of priority) {
      window.open(buildWhatsAppLink(m), "_blank");
      markSent(m.machine_serial);
      setSentSerials((s) => new Set(s).add(m.machine_serial));
      await new Promise((r) => setTimeout(r, 800));
    }
    setBulkSending(false);
    toast.success(`WhatsApp opened for ${priority.length} customers`, {
      description: "Tap Send in each tab to complete the outreach",
      duration: 4000,
    });
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const resp = await api.get("/sales/engine-care-upsell-export", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `Opsora_EngineCarePriority_${date}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  if (!data) return null;
  const k = data.kpis;
  const targets = data.targets.slice(0, 10);
  const highPriorityCount = data.targets.filter((t) => t.priority === "A").length;

  return (
    <GlassCard variant="accent" className="p-6" data-testid="engine-care-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} style={{ color: "var(--accent)" }} />
        <h3 className="font-display text-[18px]" style={{ color: "var(--slate-900)" }}>
          Engine Care Revenue Recovery
        </h3>
      </div>
      <p className="text-[12px] mb-5" style={{ color: "var(--slate-500)" }}>
        Machines missing the EQP Engine Care package — high-conversion upsell targets
      </p>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <MiniKpi
          label="Machines Without Engine Care"
          value={fmtNum(k.machines_without_engine_care)}
          sub="Eligible for upsell"
          color="amber"
        />
        <MiniKpi
          label="Projected Recovery This Quarter"
          value={fmtINR(k.projected_recovery_rs)}
          sub={`Avg ₹${fmtNum(k.package_price)} / machine`}
          color="green"
        />
        <MiniKpi
          label="High-Priority Targets"
          value={fmtNum(k.high_priority_count)}
          sub="Repeat + Renewed customers"
          color="copper"
        />
      </div>

      {/* Table */}
      <div className="glass-card-sm overflow-x-auto mb-4" data-testid="engine-care-table">
        <table className="w-full text-[12px]">
          <thead>
            <tr style={{ color: "var(--slate-500)" }}>
              {["Priority", "Serial", "Customer", "Dealer", "City", "HMR", "Age", "Pkg Value", "Actions"].map((h) => (
                <th key={h} className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {targets.map((t) => {
              const isQ = queued.has(t.machine_id);
              const isS = sentSerials.has(t.machine_serial);
              const r = REASONS[t.priority] || REASONS.C;
              return (
                <tr
                  key={t.machine_id}
                  className="border-t hover:bg-white/40"
                  style={{ borderColor: "var(--glass-border-btm)" }}
                  data-testid={`ec-row-${t.machine_id}`}
                >
                  <td className="px-3 py-2.5">
                    <span className={`status-pill ${r.cls}`} style={{ fontSize: "10px" }}>{r.label}</span>
                  </td>
                  <td className="px-3 py-2.5 font-mono">{t.machine_serial}</td>
                  <td className="px-3 py-2.5">{t.customer_name}</td>
                  <td className="px-3 py-2.5 text-[11px]" style={{ color: "var(--slate-700)" }}>{t.dealer_name}</td>
                  <td className="px-3 py-2.5">{t.city}</td>
                  <td className="px-3 py-2.5 font-mono">{t.hmr}</td>
                  <td className="px-3 py-2.5 font-mono">{t.age_years}y</td>
                  <td className="px-3 py-2.5 font-mono" style={{ color: "var(--accent)" }}>{fmtINR(t.package_value)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1.5 flex-wrap">
                      <button
                        onClick={() => toggleQueue(t.machine_id)}
                        className={isQ ? "ec-queued-btn" : "ec-add-btn"}
                        data-testid={`ec-add-${t.machine_id}`}
                      >
                        {isQ ? (
                          <span className="flex items-center gap-1"><Check size={11} /> Queued</span>
                        ) : "Add to Call List"}
                      </button>
                      {isS ? (
                        <span className="wa-sent-pill" data-testid={`wa-sent-${t.machine_id}`}>
                          <Check size={11} /> Sent
                        </span>
                      ) : (
                        <button
                          onClick={() => sendWhatsApp(t)}
                          className="wa-btn"
                          data-testid={`wa-send-${t.machine_id}`}
                        >
                          <MessageCircle size={11} /> WhatsApp
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* CTAs */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <button
          onClick={exportCsv}
          disabled={exporting}
          className="btn-copper flex items-center justify-center gap-2 py-3"
          data-testid="generate-call-list"
        >
          {exporting ? "Generating…" : (
            <>
              Generate Dealer Call List
              <ArrowRight size={14} />
            </>
          )}
        </button>
        <button
          onClick={sendAllHighPriority}
          disabled={bulkSending || highPriorityCount === 0}
          className="wa-bulk-btn py-3"
          data-testid="wa-send-all"
        >
          {bulkSending ? (
            "Opening WhatsApp tabs…"
          ) : (
            <>
              <Send size={14} />
              WhatsApp All High-Priority ({highPriorityCount})
            </>
          )}
        </button>
      </div>

      {/* Insight box */}
      <div
        className="glass-card-sm p-4 relative"
        style={{ borderLeft: "3px solid var(--accent)" }}
      >
        <div
          className="text-[9px] uppercase tracking-widest mb-1.5 font-semibold"
          style={{ color: "var(--slate-500)", letterSpacing: "0.12em" }}
        >
          Revenue Intelligence
        </div>
        <p className="font-body italic text-[12px] leading-relaxed" style={{ color: "var(--slate-700)" }}>
          Machines with EQP Engine Care active show <strong style={{ color: "var(--accent)", fontStyle: "normal" }}>2.3× higher</strong> Live Link renewal rates. Customers who take the package stay connected — and connected customers buy their next machine from the same dealer <strong style={{ color: "var(--accent)", fontStyle: "normal" }}>68% of the time</strong>.
        </p>
      </div>
    </GlassCard>
  );
}

function MiniKpi({ label, value, sub, color }) {
  const colors = {
    amber: "var(--status-amber)",
    green: "var(--status-green)",
    copper: "var(--accent)",
    red: "var(--status-red)",
  };
  return (
    <div className="glass-card-sm p-4 relative overflow-hidden">
      <div
        className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full"
        style={{ background: colors[color] || "var(--slate-500)" }}
      />
      <div className="pl-3">
        <div
          className="text-[10px] font-medium uppercase tracking-wider mb-1"
          style={{ color: "var(--slate-500)", letterSpacing: "0.06em" }}
        >
          {label}
        </div>
        <div
          className="font-display"
          style={{ fontSize: "26px", color: "var(--slate-900)", lineHeight: 1.1 }}
        >
          {value}
        </div>
        {sub && (
          <div className="text-[11px] mt-1" style={{ color: "var(--slate-500)" }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}
